import { Routes, Route, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LayoutList, ShoppingBag, Users, LogOut, ExternalLink, CalendarDays, Settings, Truck, Ticket, ListOrdered } from "lucide-react";
import AdminMedia from "@/pages/admin/AdminMedia";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminDeliveries from "@/pages/admin/AdminDeliveries";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminDiscounts from "@/pages/admin/AdminDiscounts";
import AdminPerformances from "@/pages/admin/AdminPerformances";

const NAV = [
  { to: "/admin", end: true, label: "Media Library", icon: LayoutList, testid: "admin-nav-media" },
  { to: "/admin/products", label: "Products", icon: CalendarDays, testid: "admin-nav-products" },
  { to: "/admin/performances", label: "Performance Manager", icon: ListOrdered, testid: "admin-nav-performances" },
  { to: "/admin/orders", label: "Orders", icon: ShoppingBag, testid: "admin-nav-orders" },
  { to: "/admin/deliveries", label: "Deliveries", icon: Truck, testid: "admin-nav-deliveries" },
  { to: "/admin/discounts", label: "Discounts", icon: Ticket, testid: "admin-nav-discounts" },
  { to: "/admin/customers", label: "Customers", icon: Users, testid: "admin-nav-customers" },
  { to: "/admin/settings", label: "Settings", icon: Settings, testid: "admin-nav-settings" },
];

export default function AdminPanel() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-zinc-100">
      <div className="flex">
        {/* Sidebar */}
        <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col border-r border-zinc-800 bg-[#0d0d10] p-5 md:flex">
          <div className="mb-8 flex items-center gap-2">
            <span className="h-6 w-1.5 rounded-full bg-[#ff2a5f]" />
            <span className="font-display text-lg font-extrabold uppercase tracking-tight text-white">MX Dance</span>
          </div>
          <div className="mb-2 font-mono-x text-[10px] uppercase tracking-widest text-zinc-600">Admin Console</div>
          <nav className="flex flex-1 flex-col gap-1">
            {NAV.map((n) => {
              const Icon = n.icon;
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  data-testid={n.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                      isActive ? "bg-[#ff2a5f]/15 text-[#ff2a5f]" : "text-zinc-400 hover:bg-zinc-800/60 hover:text-white"
                    }`
                  }
                >
                  <Icon className="h-4 w-4" /> {n.label}
                </NavLink>
              );
            })}
          </nav>
          <div className="space-y-2 border-t border-zinc-800 pt-4">
            <Button variant="ghost" onClick={() => navigate("/")} className="w-full justify-start text-zinc-400 hover:text-white">
              <ExternalLink className="mr-2 h-4 w-4" /> View store
            </Button>
            <Button
              data-testid="admin-logout"
              variant="ghost"
              onClick={async () => { await logout(); navigate("/"); }}
              className="w-full justify-start text-zinc-400 hover:text-white"
            >
              <LogOut className="mr-2 h-4 w-4" /> Sign out
            </Button>
            <div className="truncate pt-1 text-xs text-zinc-600">{user?.email}</div>
          </div>
        </aside>

        {/* Content */}
        <main className="min-w-0 flex-1 p-4 sm:p-6 lg:p-8">
          <Routes>
            <Route index element={<AdminMedia />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="performances" element={<AdminPerformances />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="deliveries" element={<AdminDeliveries />} />
            <Route path="discounts" element={<AdminDiscounts />} />
            <Route path="customers" element={<AdminUsers />} />
            <Route path="settings" element={<AdminSettings />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
